import { StackScreenProps } from '@react-navigation/stack'
import { useCallback, useEffect, useRef, useState } from 'react'
import { IconButton } from 'react-native-paper'
import Row from '../../../@ui/containers/grid/row'
import { ReplayLevelIndicator } from '../clusters/shared/LevelIndicator'
import { ScreenWrapper } from '../../../@ui/containers/grid/screen.wrapper'
import { useGlobalStyles } from '../../../contexts/global-styles.provider'
import { TargetType } from '../../../generated/graphql'
import { ReplayNavigatorParams } from '../../../navigation/types'
import {
  ClusterCards,
  ClusterCardsRef,
} from '../clusters/cluster-cards/components/cluster-cards-list'
import { useFocusEffect } from '@react-navigation/native'
import { Cluster } from '../cluster-list/types'
import { useReplayApi } from '../../../services/snapshots/replay-api.hook'
import { useSnapshotStore } from '../../../services/snapshots/snapshot.store.hook'
import { useReplayContext } from '../../../contexts/replay.context'
import { ClusterCardReplay } from '../clusters/cluster-cards/components/cluster-card.replay'
import { useTheme } from '../../../hooks/theme.hook'

export function ClusterCardsReplayScreen({
  navigation,
  route,
}: StackScreenProps<ReplayNavigatorParams, 'ReplayClusterCards'>) {
  const { params } = route
  const globalStyles = useGlobalStyles()
  const theme = useTheme()
  const [selectedClusterIndex, setSelectedClusterIndex] = useState<number>(0)
  const { clusterService, tick } = useReplayApi()
  const { nodeId } = useSnapshotStore()
  const [clusters, setClusters] = useState<Cluster[]>([])
  const clusterCardsRef = useRef<ClusterCardsRef>(null)
  const hasInitialized = useRef(false)

  const doRefresh = useCallback(async () => {
    const clusters = await clusterService.getClusters()
    setClusters(clusters)
  }, [clusterService])

  useEffect(() => {
    doRefresh()
  }, [tick, doRefresh])

  // Handle page lifecycle and initialization
  useFocusEffect(
    useCallback(() => {
      hasInitialized.current = false

      return () => {
        hasInitialized.current = false
      }
    }, [])
  )

  // Handle initial cluster setup
  useEffect(() => {
    if (
      hasInitialized.current ||
      clusters.length === 0 ||
      !clusterCardsRef.current
    ) {
      return
    }

    const index = clusters.findIndex((c) => c.id === params.clusterId)
    if (index < 0) {
      return
    }

    // Small delay to ensure SwipeableCards is ready
    setTimeout(() => {
      clusterCardsRef.current?.moveTo(index)
      setSelectedClusterIndex(index)
      hasInitialized.current = true
    }, 100)
  }, [clusters.length, params.clusterId])

  return (
    <ScreenWrapper
      withScrollView={false}
      style={{ ...globalStyles.pageContainer }}
    >
      <Row
        justifyContent="space-between"
        alignItems="center"
        style={{ padding: 16 }}
      >
        <IconButton
          mode={'outlined'}
          icon="arrow-left"
          onPress={() => navigation.push('ReplayClusterList', {})}
        />
        <Row gap={2} alignItems="center" columnGap={4}>
          <ReplayLevelIndicator
            target={TargetType.Cluster}
            targetId={clusters[selectedClusterIndex]?.id}
            style={{ marginLeft: 8 }}
          />
        </Row>
      </Row>

      <ClusterCards
        ref={clusterCardsRef}
        clusters={clusters}
        nodeId={nodeId}
        selectedClusterIndex={selectedClusterIndex}
        setSelectedClusterIndex={setSelectedClusterIndex}
        renderCard={(cluster, index) => (
          <ClusterCardReplay
            cluster={cluster}
            onRefresh={doRefresh}
            refreshing={false}
            key={index}
            onPerspectivePress={(perspective) => {
              navigation.navigate('ReplayPerspective', {
                articleId: perspective.id,
              })
            }}
            navigateToContributions={(perspectiveId, contributionType) => {
              navigation.navigate('ReplayPerspectiveContributions', {
                articleId: perspectiveId,
                type: contributionType,
              })
            }}
          />
        )}
      />
    </ScreenWrapper>
  )
}

export default ClusterCardsReplayScreen
